import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from 'react-bootstrap';

import '../styles/ProductCategory.css';
import { CountryContext } from "../context/CountryContext";
import StarRating from "../components/StarRating";
import BackToTop from "../components/BackToTop";
import WhatsappSupport from "../components/WhatsappSupport";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

const ProductCategory = () => {
  const { category, productType } = useParams();
  const navigate = useNavigate();
  
  const [allProducts, setAllProducts] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [offerProducts, setOfferProducts] = useState([]);
  const [, setOfferLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [productSizes, setProductSizes] = useState({});
  const [loadingSizes, setLoadingSizes] = useState({});
  const [enableSizes, setEnableSizes] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    priceSort: ''
  });

  const currencySymbols = { US: '$', UK: '£', SL: 'LKR' };
  const { country } = useContext(CountryContext);

  // Fetch company feature settings
  useEffect(() => {
    const fetchFeatureSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/company/get-feature-settings`, {
          params: { company_code: COMPANY_CODE }
        });
        setEnableSizes(response.data.enableSizes !== false);
      } catch (error) {
        console.error('Failed to fetch feature settings:', error);
        // Default to true if API fails
        setEnableSizes(true);
      }
    };
    fetchFeatureSettings();
  }, []);

  // Fetch available sizes for a product
  const fetchProductSizes = useCallback(async (styleId) => {
    if (productSizes[styleId]) {
      // Already loaded
      return;
    }

    setLoadingSizes(prev => ({ ...prev, [styleId]: true }));
    try {
      const url = `${BASE_URL}/api/customer/style-variants/${styleId}`;

      const response = await axios.get(url, {
        params: { company_code: COMPANY_CODE }
      });

      if (response.data && Array.isArray(response.data)) {
        // Extract unique sizes from variants
        const sizesSet = new Set();
        response.data.forEach(variant => {
          if (variant.size_name) {
            sizesSet.add(variant.size_name);
          }
        });
        
        const sizesList = Array.from(sizesSet).sort((a, b) => {
          // Sort sizes numerically if they are numbers, otherwise alphabetically
          const aNum = parseInt(a);
          const bNum = parseInt(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.localeCompare(b);
        });

        setProductSizes(prev => ({
          ...prev,
          [styleId]: sizesList
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product sizes:', error);
      // Set empty array on error
      setProductSizes(prev => ({
        ...prev,
        [styleId]: []
      }));
    } finally {
      setLoadingSizes(prev => ({ ...prev, [styleId]: false }));
    }
  }, [productSizes]);

  // Format display names
  const formatDisplayName = (name) => {
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  const categoryDisplay = formatDisplayName(category);
  const productTypeDisplay = formatDisplayName(productType);

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/currency/rates`);
        if (response.data.success) {
          setExchangeRates(response.data.rates);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        setExchangeRates({
          GBP: 0.75,
          LKR: 320
        });
      }
    };
    fetchExchangeRates();
  }, []);

    // fetch offer products
  useEffect(() => {
    const fetchOfferProducts = async () => {
      try {
        setOfferLoading(true);

        const response = await axios.get(`${BASE_URL}/api/customer/offers`, {
          params: {company_code: COMPANY_CODE}
        });

        // Group products by style_id and select the best offer (minimum offer_price)
        const groupedProducts = {};
        response.data.forEach(product => {
          const styleId = product.style_id;
          
          if (!groupedProducts[styleId]) {
            groupedProducts[styleId] = product;
          } else {
            // If current product has lower offer_price, replace it
            if (product.offer_price < groupedProducts[styleId].offer_price) {
              groupedProducts[styleId] = product;
            }
          }
        });

        // Convert back to array
        const uniqueProducts = Object.values(groupedProducts);
        setOfferProducts(uniqueProducts);
      } catch (error) { 
        console.error('Error fetching offer products:', error);
      } finally{
        setOfferLoading(false);
      }
    };
    fetchOfferProducts();
  }, []);

  // Timer effect for real-time updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Filter to show only active offers (started AND not ended)
  const activeOfferProducts = offerProducts.filter((p) => {
    // Check if offer has valid dates and offer_price
    if (!p.offer_start_date || !p.offer_end_date || Number(p.offer_price) <= 0) {
      return false;
    }
    
    const startDate = new Date(p.offer_start_date);
    const endDate = new Date(p.offer_end_date);
    
    // Offer must have started AND not yet ended
    return startDate <= currentTime && endDate > currentTime;
  });


  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all categories to find the main category ID
        const categoriesResponse = await axios.get(`${BASE_URL}/api/customer/main-categories`, {
          params: { company_code: COMPANY_CODE }
        });

        const mainCategories = categoriesResponse.data;
        
        // Find the matching main category
        const matchedMainCategory = mainCategories.find(
          cat => cat.category_name.toLowerCase() === category.toLowerCase()
        );

        if (!matchedMainCategory) {
          setError('Category not found');
          setLoading(false);
          return;
        }

        // Get product types (subcategories) for this main category
        const productTypesResponse = await axios.get(
          `${BASE_URL}/api/customer/product-types/${matchedMainCategory.category_id}`,
          {
            params: { company_code: COMPANY_CODE }
          }
        );
        const productTypes = productTypesResponse.data.categories || [];

        // Find the matching product type
        const matchedProductType = productTypes.find(
          type => type.category_name.toLowerCase().replace(/\s+/g, '-') === productType.toLowerCase()
        );

        if (!matchedProductType) {
          setError('Product type not found');
          setLoading(false);
          return;
        }

        // Fetch styles from the specific subcategory
        const stylesResponse = await axios.get(
          `${BASE_URL}/api/customer/styles-by-parent-category/${matchedMainCategory.category_id}`,
          {
            params: { company_code: COMPANY_CODE }
          }
        );

        // Filter styles to only include those from the specific product type
        const filteredStyles = stylesResponse.data.filter(
          style => style.category_id === matchedProductType.category_id
        );

        setAllProducts(filteredStyles);
        
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    if (category && productType) {
      fetchProducts();
    }
  }, [category, productType]);

  const getRate = () => {
    switch (country) {
      case 'US':
        return 1;
      case 'UK':
        return exchangeRates['GBP'] || 0.75;
      case 'SL':
        return exchangeRates['LKR'] || 320;
      default:
        return 1;
    }
  };

  const formatPrice = (sale_price) => {
    if (!sale_price) return "Price not defined";
    const symbol = currencySymbols[country] || '$';
    const rate = getRate();
    const convertedPrice = (sale_price * rate).toFixed(2);
    return `${symbol}${convertedPrice}`;
  };

  // Apply filters and sorting
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...allProducts];

    // Apply sorting
    switch (filters.priceSort) {
      case 'low-high':
        result.sort((a, b) => {
          const priceA = a.offer_price && a.offer_price !== 0 ? a.offer_price : (a.sale_price || 0);
          const priceB = b.offer_price && b.offer_price !== 0 ? b.offer_price : (b.sale_price || 0);
          return priceA - priceB;
        });
        break;
      case 'high-low':
        result.sort((a, b) => {
          const priceA = a.offer_price && a.offer_price !== 0 ? a.offer_price : (a.sale_price || 0);
          const priceB = b.offer_price && b.offer_price !== 0 ? b.offer_price : (b.sale_price || 0);
          return priceB - priceA;
        });
        break;
      default:
        break;
    }

    return result;
  }, [allProducts, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      priceSort: ''
    });
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.style_number}`);
  };

  const handleBackToCategory = () => {
    navigate(`/shop/${category}`);
  };

  if (loading) {
    return (
      <div className="product-category-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-category-page">
        <div className="container">
          <div className="error-message">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button 
              className="btn btn-primary pcbtn"
              onClick={() => navigate('/shop')}
            >
              Back to Shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-category-page">
      <div className="container">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav">
          <span
            className="breadcrumb-item clickable"
            onClick={() => navigate("/shop")}
          >
            Shop
          </span>
          <span className="breadcrumb-separator">/</span>
          <span
            className="breadcrumb-item clickable"
            onClick={handleBackToCategory}
          >
            {categoryDisplay}
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item current">{productTypeDisplay}</span>
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">
            {categoryDisplay} - {productTypeDisplay}
          </h1>
          <p className="page-subtitle">
            {filteredAndSortedProducts.length}{" "}
            {filteredAndSortedProducts.length === 1 ? "item" : "items"} found
            {allProducts.length !== filteredAndSortedProducts.length && (
              <span className="filtered-text">
                {" "}
                (filtered from {allProducts.length})
              </span>
            )}
          </p>
        </div>

        {/* Filter and Sort Options */}
        <div className="filter-sort-bar">
          <div className="filter-options">
            {/* Sort Dropdown */}
            <select
              className="form-select filter-select"
              value={filters.priceSort}
              onChange={(e) => handleFilterChange("priceSort", e.target.value)}
            >
              <option value="">Sort By</option>
              <option value="low-high">Price: Low to High</option>
              <option value="high-low">Price: High to Low</option>
            </select>

            {/* Clear Filters Button */}
            {filters.priceSort && (
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear Filters
              </button>
            )}
          </div>

          {/* Results count for mobile */}
          <div className="results-count-mobile">
            {filteredAndSortedProducts.length} results
          </div>
        </div>

        {/* Products Grid */}
        {filteredAndSortedProducts.length > 0 ? (
          <div className="cat-products-grid">
            {filteredAndSortedProducts.map((product) => (
              <div
                key={product.style_id}
                className="cat-product-card"
                onClick={() => handleProductClick(product)}
                onMouseEnter={() => {
                  setHoveredProductId(product.style_id);
                  fetchProductSizes(product.style_id);
                }}
                onMouseLeave={() => setHoveredProductId(null)}
              >
                <div className="cat-product-image-container">
                  <img
                    src={`${BASE_URL}/uploads/styles/${product.image}`}
                    alt={product.name}
                    className="cat-product-image"
                  />

                  <div className="cat-product-overlay">
                    <h5>Quick View</h5>
                  </div>

                  {/* Size Overlay - appears on hover */}
                  {enableSizes && (
                    <div 
                      className={`cat-product-sizes-overlay ${
                        hoveredProductId === product.style_id ? 'show' : ''
                      }`}
                    >
                      <div className="sizes-content">
                        {/* <p className="sizes-label">Available Sizes:</p> */}
                        {loadingSizes[product.style_id] ? (
                          <div className="text-center py-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          </div>
                        ) : (productSizes[product.style_id] && productSizes[product.style_id].length > 0) ? (
                          <div className="sizes-badges">
                            {productSizes[product.style_id].map((size, idx) => (
                              <Badge 
                                key={idx}
                                bg="light" 
                                text="dark"
                              >
                                {size}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted small">No sizes available</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="pc-product-description">
                    {product.description && product.description.length > 100
                      ? `${product.description.substring(0, 100)}...`
                      : product.description}
                  </p>

                  {/* Rating Display */}
                  {parseFloat(product.average_rating) > 0 &&
                    parseInt(product.review_count) > 0 && (
                      <div className="product-rating mb-2">
                        <StarRating
                          rating={Math.round(
                            parseFloat(product.average_rating)
                          )}
                          size="medium"
                        />
                        <span className="rating-text ms-2">
                          {parseFloat(product.average_rating).toFixed(1)}(
                          {parseInt(product.review_count)})
                        </span>
                      </div>
                    )}

                  <div className="shop-product-price">
                    {product.offer_price &&
                    product.offer_price !== 0 &&
                    activeOfferProducts.some(
                      (p) => p.style_id === product.style_id
                    ) ? (
                      <>
                        <span className="me-2">
                          {formatPrice(product.offer_price)}
                        </span>
                        <span className="text-muted text-decoration-line-through small">
                          {formatPrice(product.sale_price)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{formatPrice(product.sale_price)}</span>
                      </>
                    )}
                  </div>

                  {/* <div className="product-category">
                    <span className="category-badge">
                      {product.category_name}
                    </span>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-products">
            <div className="no-products-content">
              <i className="fas fa-search fa-3x"></i>
              <h3>No products found</h3>
              <p>
                We couldn't find any {productTypeDisplay.toLowerCase()} in the{" "}
                {categoryDisplay.toLowerCase()} category.
              </p>
            </div>
          </div>
        )}

        {/* Load More Button */}
        {filteredAndSortedProducts.length > 0 &&
          filteredAndSortedProducts.length % 12 === 0 && (
            <div className="load-more-container">
              <button className="btn btn-outline-primary load-more-btn">
                Load More Products
              </button>
            </div>
          )}
      </div>

      <BackToTop />
      <WhatsappSupport message="Hi, I need help with your products!" />
    </div>
  );
};

export default ProductCategory;